/**
 * CropRecommendationResults.tsx
 * 
 * Component to display crop-specific nutrient recommendations
 */

import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert
} from '@mui/material';
import Grid from '../../components/common/Grid';
import InfoIcon from '@mui/icons-material/Info';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import LocalDrinkIcon from '@mui/icons-material/LocalDrink';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import WavesIcon from '@mui/icons-material/Waves';
import { NutrientProfile } from '../../types/nutrient';

// Group elements by category
const elementCategories = {
  'Macronutrients': ['N', 'P', 'K', 'Ca', 'Mg', 'S'],
  'Micronutrients': ['Fe', 'Mn', 'Zn', 'Cu', 'B', 'Mo', 'Cl']
};

interface CropRecommendationResultsProps {
  recommendations: {
    nutrientProfile: NutrientProfile;
    environmentalRecommendations: string[];
    phRecommendation: {
      min: number;
      optimal: number;
      max: number;
      recommendation?: string;
    };
    ecRecommendation: {
      min: number;
      optimal: number;
      max: number;
      recommendation?: string;
    };
    specificNotes: string[];
    specificElementRecommendations: Record<string, string>;
  };
}

const CropRecommendationResults: React.FC<CropRecommendationResultsProps> = ({ recommendations }) => {
  if (!recommendations || !recommendations.nutrientProfile) {
    return (
      <Alert severity="info">
        No recommendations available. Please generate recommendations first.
      </Alert>
    );
  }
  
  const { 
    nutrientProfile, 
    environmentalRecommendations, 
    phRecommendation, 
    ecRecommendation, 
    specificNotes, 
    specificElementRecommendations 
  } = recommendations;
  
  const renderNutrientTable = (category: string) => {
    const elements = elementCategories[category as keyof typeof elementCategories] || [];
    
    return (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Element</TableCell>
              <TableCell align="right">Target (ppm)</TableCell>
              <TableCell>Notes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {elements.map((element) => (
              <TableRow key={element}>
                <TableCell component="th" scope="row">
                  <strong>{element}</strong>
                </TableCell>
                <TableCell align="right">
                  {nutrientProfile[element] && typeof nutrientProfile[element] === 'number' ?
                    (nutrientProfile[element] as number).toFixed(1) : 'N/A'}
                </TableCell>
                <TableCell>
                  {specificElementRecommendations[element] || ''}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };
  
  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h5" gutterBottom>
        Recommended Nutrient Profile
      </Typography>
      
      <Grid container spacing={3}>
        {/* Nutrient Targets */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Macronutrients
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {renderNutrientTable('Macronutrients')}
            </CardContent>
          </Card>
        </Grid>
        
        <Grid size={{ xs: 12, md: 6 }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Micronutrients
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {renderNutrientTable('Micronutrients')}
            </CardContent>
          </Card>
        </Grid>
        
        {/* Water Parameters */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card variant="outlined">
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <WavesIcon sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Water Parameters
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              <Grid container spacing={2}>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="subtitle2" gutterBottom>pH Range</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" sx={{ mr: 1 }}>Optimal:</Typography>
                    <Chip
                      label={phRecommendation.optimal.toFixed(1)}
                      color="primary"
                      size="small"
                    />
                  </Box>
                  <Typography variant="body2">
                    Range: {phRecommendation.min.toFixed(1)} - {phRecommendation.max.toFixed(1)}
                  </Typography>
                  {phRecommendation.recommendation && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {phRecommendation.recommendation}
                    </Typography>
                  )}
                </Grid>
                
                <Grid size={{ xs: 6 }}>
                  <Typography variant="subtitle2" gutterBottom>EC Range (mS/cm)</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" sx={{ mr: 1 }}>Optimal:</Typography>
                    <Chip
                      label={ecRecommendation.optimal.toFixed(1)}
                      color="primary"
                      size="small"
                    />
                  </Box>
                  <Typography variant="body2">
                    Range: {ecRecommendation.min.toFixed(1)} - {ecRecommendation.max.toFixed(1)}
                  </Typography>
                  {ecRecommendation.recommendation && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {ecRecommendation.recommendation}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Environmental Adjustments */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <WbSunnyIcon sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Environmental Adjustments
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              {environmentalRecommendations.length > 0 ? (
                <List dense>
                  {environmentalRecommendations.map((rec, index) => (
                    <ListItem key={index} disableGutters>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <InfoIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={rec} />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No environmental adjustments specified.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        {/* Growing Tips */}
        <Grid size={{ xs: 12 }}>
          <Card variant="outlined">
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <AgricultureIcon sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Growing Tips & Notes
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              <List>
                {specificNotes.map((note, index) => (
                  <ListItem key={index} disableGutters>
                    <ListItemIcon>
                      <InfoIcon />
                    </ListItemIcon>
                    <ListItemText primary={note} />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CropRecommendationResults;
